import { Fragment } from 'react';
import { toLocaleFormatSlash } from '../../../helpers/dateFormat';

type FormattedSupportTextProps = {
  text: string | undefined;
};

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MARKDOWN_LINK = /\[([^\]]*)]\(\s*((?:https?:\/\/|\/)[^\s)]+)\s*(?:"[^"]*")?\s*\)/gi;
const URL_IN_TEXT = /(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

const stripMarkdownLinksToUrlOnly = (value: string) => {
  return value.replace(MARKDOWN_LINK, (_match, _label: string, url: string) => url);
};

const formatLineIfIsoDate = (line: string) => {
  const t = line.trim();
  if (ISO_DATE_ONLY.test(t)) {
    return toLocaleFormatSlash(t);
  }
  return line;
};

const renderLineWithLinks = (line: string) => {
  return line.split(URL_IN_TEXT).map((segment, index) => {
    if (index % 2 === 0) {
      return <Fragment key={index}>{segment}</Fragment>;
    }

    const [trailing] = segment.match(TRAILING_PUNCTUATION) || [''];
    const url = trailing ? segment.slice(0, -trailing.length) : segment;

    return (
      <Fragment key={index}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        {trailing}
      </Fragment>
    );
  });
};

const FormattedSupportText = ({ text }: FormattedSupportTextProps) => {
  if (!text) return null;

  const normalized = stripMarkdownLinksToUrlOnly(text);
  const parts = normalized.split(/\*\*(.*?)\*\*/g);

  return (
    <>
      {parts.map((part, index) => {
        const isBold = index % 2 === 1;
        const lines = part.split('\n');

        return (
          <Fragment key={index}>
            {lines.map((line, lineIndex) => {
              const display = formatLineIfIsoDate(line);
              return (
                <Fragment key={lineIndex}>
                  {isBold ? <strong>{renderLineWithLinks(display)}</strong> : renderLineWithLinks(display)}
                  {lineIndex < lines.length - 1 && <br />}
                </Fragment>
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
};

export default FormattedSupportText;
